// ============================================
// Study Mode Component
// ============================================

import { useState, useRef } from 'preact/hooks';
import { m, AnimatePresence } from 'framer-motion';
import { Flashcard } from './Flashcard';
import { Button } from './ui/Button';
import { ProgressBar, SRSStatsBar } from './ui/ProgressBar';
import { BucketListModal } from './modals';
import { useAudio, useKeyboard } from '@/hooks';
import { tabContent, counterTick } from '@/animations';
import {
  currentCard,
  currentSRSState,
  currentLevel,
  isFlipped,
  navigationDirection,
  progressPercent,
  cardIndex,
  deck,
  dueDeck,
  drillDeck,
  drillCategory,
  exitDrill,
  newDisplay,
  learningCount,
  reviewCount,
  masteredCount,
  againCount,
  totalWords,
  masteryPercent,
  cardsStudied,
  statsVisible,
  flipCard,
  nextCard,
  prevCard,
  gradeStudyCard,
  shuffleDeck,
  resetProgress,
  toggleStats,
  audioPlaying,
  audioLoading,
  wordProgress,
  sessionNewCardLimit,
  studyMore,
  type BucketView
} from '@/state/signals';

// ============================================
// Stats Panel
// ============================================

function StatsPanel() {
  const stats = {
    new: newDisplay.value,
    learning: learningCount.value,
    review: reviewCount.value,
    mastered: masteredCount.value
  };
  const total = totalWords.value;
  const mastery = masteryPercent.value;
  const studied = cardsStudied.value.size;
  const needsWork = againCount.value;

  // Which bucket/again list is open (null = closed).
  const [openView, setOpenView] = useState<BucketView | null>(null);
  // Close the list when the level changes underneath it (avoids showing a
  // stale level's words). Render-time reset instead of a banned useEffect:
  // reading currentLevel.value subscribes us, and we reconcile against the
  // last-seen level on the render that follows a switch.
  const level = currentLevel.value;
  const lastLevel = useRef(level);
  if (lastLevel.current !== level) {
    lastLevel.current = level;
    if (openView !== null) setOpenView(null);
  }

  return (
    <m.div
      variants={tabContent}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="bg-white rounded-xl shadow-lg p-6 mb-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          📈 Learning Analytics
        </h3>
        <button
          type="button"
          onClick={toggleStats}
          aria-label="Close stats"
          className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
          <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor" aria-hidden="true">
            <path d="M9.77 2.23a.75.75 0 0 0-1.06 0L6 4.94 3.29 2.23a.75.75 0 0 0-1.06 1.06L4.94 6 2.23 8.71a.75.75 0 0 0 1.06 1.06L6 7.06l2.71 2.71a.75.75 0 0 0 1.06-1.06L7.06 6l2.71-2.71a.75.75 0 0 0 0-1.06z" />
          </svg>
        </button>
      </div>

      {/* SRS Progress Bar — visual proportion only. Its count legend is
          suppressed because the tappable cards below show the same numbers. */}
      <div className="mb-4">
        <SRSStatsBar stats={stats} total={total} showLegend={false} />
      </div>

      {/* Stat Cards — tap to see the words behind each count. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <StatCard label="New" value={stats.new} color="bg-srs-new" textColor="#1F2A3A" view="new" onOpen={setOpenView} />
        <StatCard label="Learning" value={stats.learning} color="bg-srs-learning" textColor="#1F2A3A" view="learning" onOpen={setOpenView} />
        <StatCard label="Review" value={stats.review} color="bg-srs-review" textColor="#FFFFFF" view="review" onOpen={setOpenView} />
        <StatCard label="Mastered" value={stats.mastered} color="bg-srs-mastered" textColor="#1F2A3A" view="mastered" onOpen={setOpenView} />
      </div>

      {/* "Again" list — words last marked ✗ Again (the second section Violette
          asked for). Hidden when there's nothing to redo. */}
      {needsWork > 0 && (
        <button
          type="button"
          onClick={() => setOpenView('again')}
          className="w-full mb-6 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-srs-review/10 text-srs-review text-sm font-medium hover:bg-srs-review/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
          ⚠ Needs work ({needsWork}) — words you marked ✗ Again
        </button>
      )}

      <BucketListModal view={openView} onClose={() => setOpenView(null)} />

      {/* Session stats — one quiet line. Dropped the low-value "flips" metric;
          lifetime numbers live in LevelProgress (always visible). */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm text-gray-600">
          <strong>This session:</strong> {studied} studied · {mastery}% mastery
          <span className="ml-1 text-xs text-gray-400">(resets on reload)</span>
        </p>
      </div>
    </m.div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  textColor: string;
  view: BucketView;
  onOpen: (view: BucketView) => void;
}

function StatCard({ label, value, color, textColor, view, onOpen }: StatCardProps) {
  // Empty buckets are de-emphasized so the eye lands on the active ones; still
  // tappable (the list modal shows a friendly "nothing here yet" state).
  const muted = value === 0;
  return (
    <button
      type="button"
      onClick={() => onOpen(view)}
      aria-label={`Show ${label} words`}
      className={`text-center rounded-lg p-1 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${muted ? 'opacity-40' : ''}`}
    >
      <m.div
        className="text-2xl font-bold text-gray-800"
        variants={counterTick}
        initial="initial"
        animate="animate"
        key={value}
      >
        {value}
      </m.div>
      <div
        className={`text-xs font-medium px-2 py-1 rounded-full ${color} mt-1`}
        style={{ color: textColor }}
      >
        {label}
      </div>
    </button>
  );
}

// ============================================
// Navigation Controls
// ============================================

function NavigationControls() {
  const flipped = isFlipped.value;
  // In a drill this is PRACTICE: never show the grade buttons (they call
  // updateWordSRS). Keep plain Prev/Flip/Next so cramming can't change SRS.
  const drilling = drillDeck.value !== null;

  return (
    <div className="flex flex-col gap-3 mt-6">
      {/* Main Navigation — before flip: Prev / Flip / Next.
          After flip: grade the card (drives the SRS) via Again / Got it.
          During a drill: always Prev / Flip / Next (no grading). */}
      {flipped && !drilling ? (
        <div className="flex justify-center gap-3">
          <Button
            variant="secondary"
            onClick={() => gradeStudyCard(false)}
            ariaLabel="Mark incorrect and go to next card"
          >
            ✗ Again
          </Button>
          <Button
            variant="primary"
            onClick={() => gradeStudyCard(true)}
            ariaLabel="Mark correct and go to next card"
          >
            ✓ Got it
          </Button>
        </div>
      ) : (
        <div className="flex justify-center gap-3">
          <Button
            variant="secondary"
            onClick={prevCard}
            ariaLabel="Previous card"
          >
            ← Previous
          </Button>
          <Button
            variant="primary"
            onClick={flipCard}
          >
            Flip Card
          </Button>
          <Button
            variant="secondary"
            onClick={nextCard}
            ariaLabel="Next card"
          >
            Next →
          </Button>
        </div>
      )}

      {/* Utility Controls */}
      <div className="flex justify-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={shuffleDeck}
        >
          🔀 Shuffle
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetProgress}
        >
          🔄 Reset
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleStats}
        >
          📊 Stats
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Main Study Mode Component
// ============================================

export function StudyMode() {
  const card = currentCard.value;
  const flipped = isFlipped.value;
  const direction = navigationDirection.value;
  const srsState = currentSRSState.value;
  const progress = progressPercent.value;
  const idx = cardIndex.value;
  // Active deck for study = dueDeck (SRS-aware queue, not full vocab) — or the
  // drill set when a focused practice session is active.
  const drilling = drillDeck.value !== null;
  const drillCount = drillDeck.value?.length ?? 0;
  const drillLabel = drillCategory.value === 'again' ? 'Again' : 'Review';
  const dueSize = dueDeck.value.length;
  // Size of whatever deck is actually on screen — drives the counter and the
  // "all caught up" guard so a drill (with no due cards) doesn't read as empty.
  const activeSize = drilling ? drillCount : dueSize;
  const totalDeckSize = deck.value.length;
  const showStats = statsVisible.value;
  const isAudioPlaying = audioPlaying.value;
  const isAudioLoading = audioLoading.value;

  const { playAudio } = useAudio();

  // Keyboard navigation
  useKeyboard();

  const handlePlayAudio = () => {
    if (card) {
      playAudio(card.japanese);
    }
  };

  // Empty-state guards. Three sub-states:
  //   1. Vocabulary still loading (deck empty)             → "Loading…"
  //   2. Vocabulary loaded but no cards due right now      → "All caught up"
  //   3. Card present                                      → render flashcard
  if (totalDeckSize === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px] text-gray-500">
        <p>Loading vocabulary…</p>
      </div>
    );
  }

  if (!card || activeSize === 0) {
    // Compute whether there are unseen new cards beyond the current session cap.
    // Mirror dueDeck.ts logic: a word is "new" if its progress entry is missing
    // or its state is 'new'. Count all such words in the full vocab deck, then
    // check if that count exceeds the current session limit.
    const hasMoreNewCards = (() => {
      if (import.meta.env.VITE_FEATURE_SESSION_CONTROLS !== 'true') return false;
      const progress = wordProgress.value;
      const words = deck.value;
      const limit = sessionNewCardLimit.value;
      let newCardCount = 0;
      for (const word of words) {
        const p = progress[word.japanese];
        if (!p || p.state === 'new') {
          newCardCount++;
        }
      }
      return newCardCount > limit;
    })();

    return (
      <m.div
        variants={tabContent}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="flex flex-col items-center justify-center min-h-[300px] text-center px-4"
      >
        <div className="text-5xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-slate-700 mb-2">All caught up!</h2>
        <p className="text-slate-500 mb-6 max-w-md">
          No cards are due right now. Come back later for your next review, or
          switch to Quiz mode to practice with your full vocabulary.
        </p>
        {import.meta.env.VITE_FEATURE_SESSION_CONTROLS === 'true' && hasMoreNewCards && (
          <Button
            variant="secondary"
            onClick={studyMore}
          >
            Study 10 more
          </Button>
        )}
      </m.div>
    );
  }

  return (
    <m.div
      variants={tabContent}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="w-full"
    >
      {/* Drill (practice) banner — visible only during a focused session.
          Signals that grading is off and offers a one-tap exit. */}
      {drilling && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-srs-review/10 px-3 py-2">
          <span className="text-sm font-medium text-srs-review">
            🎯 Practicing: {drillLabel} ({drillCount}) · grading paused
          </span>
          <button
            type="button"
            onClick={exitDrill}
            className="shrink-0 text-sm font-semibold text-srs-review underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded"
          >
            Exit
          </button>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center text-sm text-gray-600 mb-1">
          {/* Session position only. Lifetime reviews/mastery live in the
              always-visible LevelProgress caption above — don't duplicate them
              here (was a redundant pill restating "N reviews"). */}
          <span>Card {idx + 1} of {activeSize}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <ProgressBar value={progress} />
      </div>

      {/* Stats Panel (collapsible) */}
      <AnimatePresence>
        {showStats && <StatsPanel />}
      </AnimatePresence>

      {/* Flashcard */}
      <Flashcard
        word={card}
        isFlipped={flipped}
        onFlip={flipCard}
        srsState={srsState}
        direction={direction}
        onPlayAudio={handlePlayAudio}
        isAudioPlaying={isAudioPlaying}
        isAudioLoading={isAudioLoading}
      />

      {/* Navigation Controls */}
      <NavigationControls />

      {/* Keyboard shortcuts hint */}
      <div className="text-center text-xs text-gray-400 mt-4">
        <span className="hidden sm:inline">
          Keyboard: Space/Enter (flip) • ←/→ (navigate) • 1 (again) / 2 (got it) after flip • K (toggle kana)
        </span>
      </div>
    </m.div>
  );
}
